import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import {
  ManageResourceTagDisplayModel,
  Perspectives,
  ResourceTagFilterDropdownItems,
  ResourceTagViewerProps
} from "@app/tags/types";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { Modal } from "@app/shared/Modals";
import { FlyoutLink } from "@app/shared/Flyouts";
import ResourceTagPanel from "@app/tags/ResourceTagPanel";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { SearchBox } from "@app/shared/SearchBox";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

type ResourceTagViewerWrappedProps = ResourceTagViewerProps;

const ResourceTagViewer: FC<ResourceTagViewerWrappedProps> = (props: ResourceTagViewerWrappedProps) => {
  const user = useContext(UserContext);
  const title = "Tag Management";
  const resourceTaggingService = useRef(new ResourceTaggingService()).current;

  const [deleteConfirmationModalIsOpen, setDeleteConfirmationModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [perspective, setPerspective] = useState<keyof typeof Perspectives>(props.perspective || Perspectives.Group);
  const [resourceTags, setResourceTags] = useState<ManageResourceTagDisplayModel[]>([]);
  const [searchValue, setSearchValue] = useState(props.searchValue || "");
  const [selectedRows, setSelectedRows] = useState<ManageResourceTagDisplayModel[]>([]);
  const [showXRows, setShowXRows] = useState(props.showXRows || props.take || 100);
  const [tableRows, setTableRows] = useState<ManageResourceTagDisplayModel[]>([]);
  const [take] = useState(props.take || 100);

  useEffect(() => {
    handleViewBySwitch(ResourceTagFilterDropdownItems.find((item) => item.key === perspective));
  }, []);

  const selectedKeys: string[] = [...new Set(selectedRows.map((sr: ManageResourceTagDisplayModel) => sr.key))];
  const groupName: Perspectives = ResourceTagFilterDropdownItems.find((item) => item.key === perspective).key;
  const numRecs: number = selectedKeys.length;

  const deleteChecked = async (groupName: Perspectives, selectedKeys: number[]): Promise<void> => {
    setLoading(true);
    setSelectedRows([]);
    hideModal();
    await deleteTags(groupName, selectedKeys);
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    const data = tableRows.slice(0, showXRows);
    const newSelectedRows: ManageResourceTagDisplayModel[] = data.filter((p) => selectedKeys.includes(p.key));
    setSelectedRows(newSelectedRows);
  }

  const showModal = (): void => {
    setDeleteConfirmationModalIsOpen(true);
  }

  const hideModal = (): void => {
    setDeleteConfirmationModalIsOpen(false);
  }

  const deleteTags = async (typeName: Perspectives, selectedKeys: number[]): Promise<void> => {
    const data = tableRows.slice(0, showXRows);
    const associatedKeys = data.filter((p => selectedKeys.map(key => key + '').includes(p.key)))
      .flatMap(p => p.resourceTagIds)
      .map(key => parseInt(key));
    await resourceTaggingService.deleteTags(associatedKeys);
    getResourceTags(typeName);
  }

  const downloadTags = (): void => {
    resourceTaggingService.downloadTags()
      .then((data) => {
        const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "downloadtags.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  // Getters
  const getResourceTags = (targetPerspective: string) => {
    setLoading(true);
    setTableRows([]);

    resourceTaggingService.getClientProjectTagInfo(targetPerspective as Perspectives ?? perspective as Perspectives).then((response: ManageResourceTagDisplayModel[]) => {
      const tempResourceTags: ManageResourceTagDisplayModel[] = response.map(r => convertResourceTagModelToRow(r));
      setLoading(false);
      setPerspective(targetPerspective as Perspectives);
      setResourceTags(tempResourceTags);
      setTableRows(tempResourceTags);
    });

    function convertResourceTagModelToRow(resourceTagModel: ManageResourceTagDisplayModel): ManageResourceTagDisplayModel {
      return {
        ...resourceTagModel,
        [wafflesKeyColumnName]: resourceTagModel.key.toString(),
      };
    }
  };

  // Builders
  const buildColumnConfig = (): ColumnConfig => {
    const showGroupCols = perspective == Perspectives.Group;
    const showTagCols = perspective == Perspectives.Tag;
    const showProjectCols = perspective == Perspectives.Project;
    return {
      /* oxlint-disable sort-keys */
      tagGroupName: {
        displayText: "Group Name",
        hidden: !showGroupCols,
        sortOrder: 1,
        testId: "resource-tag-viewer__tags-waffles__group-name-column",
      },
      tagName: {
        displayText: "Tag Name",
        hidden: !(showTagCols || showGroupCols),
        sortOrder: 2,
        testId: "resource-tag-viewer__tags-waffles__tag-name-column",
      },
      projectName: {
        displayText: "Project Name",
        hidden: !showProjectCols,
        sortOrder: 3,
        testId: "resource-tag-viewer__tags-waffles__project-count-column",
      },
      templateName: {
        displayText: "Template Name",
        hidden: !showProjectCols,
        sortOrder: 4,
        testId: "resource-tag-viewer__tags-waffles__template-name-column",
      },

      projectCount: {
        displayText: "Number of Projects",
        hidden: !(showGroupCols || showTagCols),
        sortOrder: 5,
        testId: "resource-tag-viewer__tags-waffles__project-count-column",
      },
      groupCount: {
        displayText: "Number of Groups",
        hidden: !showTagCols,
        sortOrder: 7,
        testId: "resource-tag-viewer__tags-waffles__group-count-column",
      },
      tagCount: {
        displayText: "Number of Tags",
        hidden: !(showProjectCols || showGroupCols),
        sortOrder: 6,
        testId: "resource-tag-viewer__tags-waffles__tag-count-column",
      },
      /* oxlint-enable sort-keys */
    };
  }

  const buildSearchDescriptor = (): string => {
    const params = ResourceTagFilterDropdownItems.find((item) => item.key === perspective).name;

    return `This will search each row's ${params} name.`;
  }

  // Handlers
  const handleCustomSort = (rows: ManageResourceTagDisplayModel[], column: keyof ManageResourceTagDisplayModel, direction: SortDirections): ManageResourceTagDisplayModel[] => {
    setLoading(true);
    rows = rows ?? tableRows;

    if (direction === SortDirections.None) {
      handleSearch(searchValue);
      return;
    } else {
      const multiplier = direction === SortDirections.Asc ? 1 : -1;
      const sorted = [...rows].sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      setLoading(false);
      setTableRows(sorted);
      return sorted;
    }
  }

  const handleLoadMore = (justChecking: boolean): boolean => {
    let targetRows = showXRows;
    if (!justChecking) {
      targetRows += take;
      setShowXRows(targetRows);
    }
    return tableRows.length > targetRows;
  }

  const handleSearch = (newSearchValue: string) => {
    setLoading(true);

    const newRows: ManageResourceTagDisplayModel[] = [];
    resourceTags.forEach((r) => {
      let searchFields: string[];
      switch (perspective) {
        case Perspectives.Group:
          searchFields = [r.tagGroupName, r.tagName];
          break;
        case Perspectives.Tag:
          searchFields = [r.tagName];
          break;
        case Perspectives.Project:
          searchFields = [r.projectName, r.templateName];
          break;
      }
      if (searchFields.some(x => !!x && x.toLowerCase().includes(newSearchValue.toLowerCase())))
        newRows.push(r);
    });
    setLoading(false);
    setSearchValue(newSearchValue);
    setShowXRows(take);
    setTableRows(newRows);
  }

  const handleViewBySwitch = (tempSelectedItem: DropdownItem<string>): void => {
    setPerspective(tempSelectedItem.key as Perspectives);
    setSearchValue("");
    setShowXRows(take);

    getResourceTags(tempSelectedItem.key);
    $("#resource-tag-viewer__search__input").val("");
  }

  return (
    <Page documentTitle={title}>
      <Breadcrumbs template={BreadcrumbTemplateEnum.Tags} items={[]} />
      <PageInner>
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0 px-0 col-auto">{title}</h1>
          {resourceTags.length != 0 &&
            <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
              <QESButton style={{ marginLeft: "auto", marginRight: 5 }} qesTestId={"resource-tag-viewer__download-tags-button"}
                onClick={() => downloadTags()} className="col-auto">Download Existing Tags</QESButton>
            </RoleRestricted>
          }
          <Link to="./import" className="col-auto px-0"><QESButton qesTestId={"resource-tag-viewer__upload-tags-button"}>Upload Tags</QESButton></Link>
        </header>
        <PageSection>
          {resourceTags.length != 0 &&
            <SearchBox
              fieldsetClass="d-flex"
              inputLabel={"Search"}
              descriptiveText={buildSearchDescriptor()}
              onSubmit={handleSearch}
              testId="resource-tag-viewer__search"
              alwaysShowClearButton={true}
            />
          }
          <div className="mt-3 container-sm ms-0">
            <div className="d-flex justify-content-between">
              <div className="flex-grow-0">
                {resourceTags.length != 0 &&
                  <NewActionMenu
                    buttonClassNames={"qes-button-secondary"}
                    buttonText={`View by: ${ResourceTagFilterDropdownItems.find((item) => item.key === perspective).name}`}
                    key={perspective}
                    testId="resource-tag-viewer__view-by-dropdown"
                  >
                    {ResourceTagFilterDropdownItems.map((item) => {
                      const isSelected = item.key === perspective;
                      return (
                        <NewActionMenuItem
                          display={item.name}
                          isSelected={isSelected}
                          key={item.key}
                          onClick={() => handleViewBySwitch(item)}
                          testId={`resource-tag-viewer__view-by-option--${item.key}`}
                        />
                      )
                    })}
                  </NewActionMenu>

                }
              </div>
              {user.email.endsWith("questanalytics.com") &&
                <div>
                  <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                    <FlyoutLink
                      content={{
                        body: <ResourceTagPanel />,
                        title: "Tags",
                      }}
                      testId="resource-tag-review-flyout-link"
                      size="40"
                      classNames="qes-button-secondary p-0"
                      onClose={() => {
                        handleViewBySwitch(ResourceTagFilterDropdownItems.find((item) => item.key === perspective));
                      }}
                    >
                      Add Tags
                    </FlyoutLink>
                    <QESButton qesType="secondary-danger" qesTestId='delete-button' onClick={() => showModal()}
                      disabled={numRecs === 0}>
                      Delete Tags
                    </QESButton>
                  </RoleRestricted>
                </div>
              }
            </div>
            <div>&nbsp;</div>
            <Modal
              isVisible={deleteConfirmationModalIsOpen}
              content={{
                body: "Are you sure you want to delete the selection?",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => deleteChecked(groupName, selectedKeys.map(Number)),
                confirmButtonText: "Yes",
                title: "Delete"
              }}
              closeModal={() => hideModal()}
            />
            <Waffles<ManageResourceTagDisplayModel>
              columns={buildColumnConfig()}
              customSort={handleCustomSort}
              data={tableRows.slice(0, showXRows)}
              emptyTableMessage={"No Tags Available"}
              loadMore={handleLoadMore}
              fullWidth={true}
              loading={loading}
              passThroughSorting={true}
              showCheckboxes={true}
              selectedKeys={selectedKeys}
              handleCheckboxChange={handleCheckboxChange}
              testId="resource-tag-viewer__tags-waffles"
            />
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
}

export default ResourceTagViewer;